import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../../shared/usersForDropdown";
import {
  fetchLocationsForDropdown,
  fetchLocationZonesForDropdown,
} from "../../../shared/locationDropdowns";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import ReceivingAssemblyItemDetails from "../../../components/forms/receiving/invoices/ReceivingAssemblyItemDetails";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";
function purchaseOrderFromGetResponse(data) {
  if (!data) return null;
  return data.purchase_order ?? data;
}

export function AddReceivingInvoiceViewModel(
  logout,
  addSnackbar,
  purchaseOrderId = null,
) {
  const [barcodeDetails, setBarcodeDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [invoice, setInvoice] = useState({
    id: 0,
    supplier_name: "",
    invoice_number: "",
  });
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [linkedPurchaseOrder, setLinkedPurchaseOrder] = useState(null);
  const [purchaseOrderOptions, setPurchaseOrderOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);

  const loadLinkedPurchaseOrder = async function () {
    if (!purchaseOrderId) return false;
    const callResult = await makePostApiCall(
      `purchaseOrders/${purchaseOrderId}`,
      {},
    );
    if (callResult.success) {
      setLinkedPurchaseOrder(purchaseOrderFromGetResponse(callResult.data));
      return true;
    }
    if (callResult.errorStatus === 401) {
      logout();
      return false;
    }
    showErrorModal(
      callResult.data?.message || "Could not load purchase order.",
    );
    return false;
  };

  const init = async function () {
    setLoading(true);
    try {
      const tasks = [
        getUsersForDropdown(),
        getSuppliersForDropdown(),
        getLocationsForDropdown(),
      ];

      if (purchaseOrderId) {
        tasks.push(loadLinkedPurchaseOrder());
      } else {
        tasks.push(getPurchaseOrderForDropdown());
      }

      await Promise.all(tasks);
    } finally {
      setLoading(false);
    }
  };

  const createReceivingInvoice = async function (values) {
    // First create the invoice, then create its sub items...
    const payload = {
      ...values,
      ...(purchaseOrderId
        ? { purchase_order_id: Number(purchaseOrderId) }
        : {}),
    };
    let callResult = await makePostApiCall("createReceivingInvoice", payload);

    if (callResult.success) {
      setInvoice(callResult.data.receiving_invoice);
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getAllAssemblyItems = async function (supplierId) {
    const payload = {}; // supplier_id: supplierId ? supplierId : null
    let callResult = await makePostApiCall(`allAssemblyItems`, payload);

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onGenerateBarcodePdf = async function (values) {
    const payload = { ...values };

    let callResult = await makePostApiCall("createBarcodePdf", payload);

    if (callResult.success) {
      setBarcodeDetails(callResult.data);
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return callResult;
    }
  };

  const getAssemblyItemByPlu = async function (plu) {
    const payload = { plu: plu };
    let callResult = await makePostApiCall(`assemblyItems/plu/${plu}`, payload);

    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return false;
    }
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const showReceivingItemsDeleteModal = (message) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} title={message} />
      ),
      open: true,
    });
  };

  const updateReceivingAssemblyCall = async function (values, id) {
    let callResult = await makePostApiCall(
      `updateReceivingAssemblyItem/${id}`,
      values,
    );

    if (callResult.success) {
      setSlideOver({
        childComponent: null,
        open: false,
      });

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      await getInvoiceProducts(invoice.id);

      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return false;
    }
  };

  const openReceivingItemDetails = async function (receivingItemId) {
    let callResult = await makePostApiCall(`receivingItem/${receivingItemId}`);

    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <ReceivingAssemblyItemDetails
            onGenerateBarcodePdf={onGenerateBarcodePdf}
            receivingItem={callResult.data.receiving_item}
            setOpen={setSlideOver}
            updateReceivingAssemblyCall={updateReceivingAssemblyCall}
          />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const deleteReceivingItem = async function (receivingItemId) {
    let callResult = await makePostApiCall(
      `deleteReceivingItem/${receivingItemId}`,
    );

    if (callResult.success) {
      // Reload products to get fresh totals from backend
      await getInvoiceProducts(invoice.id);

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }

    return callResult;
  };

  const deleteMultipleReceivingItems = async function (
    receivingInvoiceId,
    itemIds,
    reason,
  ) {
    const payload = {
      receiving_invoice_id: receivingInvoiceId,
      items: (itemIds || []).map((id) => ({ id })),
      reason: reason || null,
    };

    let callResult = await makePostApiCall(
      "deleteMultipleReceivingAssemblyItems",
      payload,
    );

    if (callResult.success) {
      await getInvoiceProducts(receivingInvoiceId);

      const message = callResult.data.message;

      if (message.length > 1) {
        showReceivingItemsDeleteModal(message);
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            "Items deleted successfully.",
            SnackbarType.Success,
          ),
        );
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }

    return callResult;
  };

  const getUsersForDropdown = async function () {
    let callResult = await fetchUsersForDropdown([1, 2], [1, 4]);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getSuppliersForDropdown = async function () {
    let callResult = await makeGetApiCall(`manufacturersForDropdown`);

    if (callResult.success) {
      setSuppliers(callResult.data.suppliers);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers.");
    }
  };

  const getInvoiceProducts = async function (invoiceId) {
    var invoiceIdToRequest = invoiceId ? invoiceId : invoice.id;
    let callResult = await makeGetApiCall(
      `invoiceProducts/${invoiceIdToRequest}`,
    );
    if (callResult.success) {
      setInvoiceItems(callResult.data.receiving_items);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getPurchaseOrderForDropdown = async function () {
    let callResult = await makeGetApiCall(`purchaseOrdersForDropdown`);
    if (callResult.success) {
      setPurchaseOrderOptions(callResult.data);
    } else {
      showErrorModal("Could not fetch purchase orders.");
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getLocationsForDropdown = async function () {
    const callResult = await fetchLocationsForDropdown();
    if (callResult.success) {
      const options = callResult.data || [];
      setLocationOptions(options);
      return options;
    }
    if (callResult.errorStatus === 401) {
      logout();
      return [];
    }
    showErrorModal(callResult.data?.message || "Could not fetch locations.");
    return [];
  };

  const loadZonesForLocation = async function (location) {
    if (!location?.value) {
      setZoneOptions([]);
      return [];
    }
    const callResult = await fetchLocationZonesForDropdown(location);
    if (callResult.success) {
      const options = callResult.data || [];
      setZoneOptions(options);
      return options;
    }
    if (callResult.errorStatus === 401) {
      logout();
      return [];
    }
    showErrorModal(callResult.data?.message || "Could not fetch zones.");
    setZoneOptions([]);
    return [];
  };

  return {
    barcodeDetails,
    linkedPurchaseOrder,
    purchaseOrderId,
    loadLinkedPurchaseOrder,
    init,
    createReceivingInvoice,
    getAllAssemblyItems,
    getAssemblyItemByPlu,
    invoice,
    invoiceItems,
    setInvoiceItems,
    onGenerateBarcodePdf,
    productTypeOptions,
    loading,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    emptyModal,
    showErrorModal,
    openReceivingItemDetails,
    deleteReceivingItem,
    deleteMultipleReceivingItems,
    getUsersForDropdown,
    employeeOptions,
    getSuppliersForDropdown,
    suppliers,
    getInvoiceProducts,
    getPurchaseOrderForDropdown,
    purchaseOrderOptions,
    getLocationsForDropdown,
    locationOptions,
    zoneOptions,
    loadZonesForLocation,
  };
}
